import { AxiosInstance } from "axios";
import { Identifier, useNotify } from "react-admin";
import { useHistory } from "react-router-dom";
import { GeometrisCommand } from ".";
import { handleAxiosError } from "../../../common/data/axiosErrorHandler";
import { GeometrisApiApiFactory } from "../../../common/data/service-proxies";
import { useAxiosInstance } from "../../../common/hooks/useAxiosInstance";
import { EditActions } from "../../common/form/FormActions";
import { HookFormCreateView } from "../../common/form/hook-form/HookFormCreateView";
import { GeometrisCustomCommandForm } from "./GeometrisCustomCommandForm";
import { GeometrisEngineBusCommandForm } from "./GeometrisEngineBusCommandForm";
import { GeometrisResetOdometerCommandForm } from "./GeometrisResetOdometerCommandForm";
import { GeometrisSelectDeviceCommandForm } from "./GeometrisSelectDeviceCommandForm";

interface Props {
  id?: Identifier;
}

const getTitle = (id: GeometrisCommand) => {
  switch (id) {
    case "custom-command":
      return "Custom Geometris API device command";
    case "engine-bus-command":
      return "Change ELD engine bus protocol";
    case "reset-command":
      return "Reset ELD";
    case "reset-odometer-command":
      return "Set ELD odometer";
    case "shipmode-command":
      return "Completely power down all ELD systems";
    case "send-device-configuration":
      return "Send device configuration";
  }
};

const getForm = (id: GeometrisCommand) => {
  switch (id) {
    case "custom-command":
      return <GeometrisCustomCommandForm />;
    case "engine-bus-command":
      return <GeometrisEngineBusCommandForm />;
    case "reset-command":
      return <GeometrisSelectDeviceCommandForm />;
    case "reset-odometer-command":
      return <GeometrisResetOdometerCommandForm />;
    case "shipmode-command":
      return <GeometrisSelectDeviceCommandForm />;
    case "send-device-configuration":
      return <GeometrisSelectDeviceCommandForm />;
  }
};

const getAction = (id: GeometrisCommand, axios: AxiosInstance) => {
  switch (id) {
    case "custom-command":
      return GeometrisApiApiFactory(undefined, axios.defaults.baseURL, axios)
        .apiAppGeometrisapiSenddevicecommandPost;

    case "engine-bus-command":
      return GeometrisApiApiFactory(undefined, axios.defaults.baseURL, axios)
        .apiAppGeometrisapiSetenginebustypePost;

    case "reset-command":
      return GeometrisApiApiFactory(undefined, axios.defaults.baseURL, axios)
        .apiAppGeometrisapiSendresetcommandPost;

    case "reset-odometer-command":
      return GeometrisApiApiFactory(undefined, axios.defaults.baseURL, axios)
        .apiAppGeometrisapiSetodometervaluePost;

    case "shipmode-command":
      return GeometrisApiApiFactory(undefined, axios.defaults.baseURL, axios)
        .apiAppGeometrisapiSendshipmodecommandPost;

    case "send-device-configuration":
      return GeometrisApiApiFactory(undefined, axios.defaults.baseURL, axios)
        .apiAppGeometrisapiSetdeviceconfigurationPost;
  }
};

export const GeometrisCommandForm = ({ id: identifier }: Props) => {
  const history = useHistory();
  const id = identifier as GeometrisCommand;
  const axios = useAxiosInstance();
  const notify = useNotify();
  if (!id) {
    return null;
  }

  const form = getForm(id);
  if (!form) {
    return null;
  }

  return (
    <HookFormCreateView
      onSuccess={() => {
        history.push("/admin-geometris-commands");
      }}
      variant="outlined"
      margin="dense"
      className="form-create-edit"
      record={{}}
      resource="admin-geometris-commands"
      title={getTitle(id)}
      actions={<EditActions />}
      createMethodOverride={(data) => {
        if (!axios) {
          return Promise.reject("Axios was undefined");
        }

        const action = getAction(id, axios);
        if (!action) {
          return Promise.reject("Unable to resolve the action");
        }

        return action(data)
          .then(() =>
            notify(
              "resources.admin-geometris-commands.labels.commandSentSuccessfully",
              { type: "success" }
            )
          )
          .catch((e) => {
            console.error(handleAxiosError(e));
            notify("resources.admin-geometris-commands.labels.commandFailed", {
              type: "error",
            });
          });
      }}
    >
      {form}
    </HookFormCreateView>
  );
};
